import PageHeading from "../../../components/desktop/PageHeading";
import React, {
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FiCalendar } from "react-icons/fi";
import { toast } from "react-toastify";
import "./Report.css";
import ExcelJS from "exceljs";
import { api } from "../../../utils/api";
import { useAuthStore } from "../../../store/authStore";
import { useLoaderStore } from "../../../store/loaderStore";
import { resolveReportLocations } from "../../../utils/reportLocation";

// ── Default: current month (YYYY-MM-DD) ──
const getDefaultMonth = () => {
  const today = new Date();
  const first = new Date(today.getFullYear(), today.getMonth(), 1);
  const last = new Date(today.getFullYear(), today.getMonth() + 1, 0);

  const fmt = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
  };

  return { fd: fmt(first), td: fmt(last) };
};

// ── Format "YYYY-MM-DD" → "DD-MM-YYYY" ──
const formatDate = (value) => {
  if (!value) return "-";

  return value.split("-").reverse().join("-");
};

// ── Format "YYYY-MM-DD" → "DD/MM/YYYY" (for the Excel export header) ──
const formatDateSlash = (value) => {
  if (!value) return "-";

  return value.split("-").reverse().join("/");
};

// ── Account name shown in the Excel export header, keyed by company_id. ──
// There is no company-name field anywhere in the API response today, so this
// mirrors the same company_id → value lookup already used for the logo in
// src/components/desktop/Header.js.
const EXPORT_ACCOUNT_NAMES = {
  1: "KTPL",
  2: "Star Engineers (I) Pvt Ltd",
};

// Number of columns in the exported table (Sr. No. … Status), used to merge
// the info-block values across the full row width.
const EXPORT_COLUMN_COUNT = 10;

// ── Shared Intl instances ──
// Constructing an Intl object is one of the slowest things the engine does,
// so these are built once for the module's lifetime. Never create one inside
// a render pass or a sort comparator.
const TIME_FORMATTER = new Intl.DateTimeFormat("en-GB", {
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

const CODE_COLLATOR = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});

// ── Dropdown virtualisation ──
// The dropdown lists every matching user, but only the rows inside the visible
// scroll window are mounted. ROW_HEIGHT must stay in sync with the fixed height
// on `.report-user-dropdown-item` in Report.css, and DROPDOWN_VIEWPORT with its
// max-height on `.report-user-dropdown`.
const ROW_HEIGHT = 32;
const DROPDOWN_VIEWPORT = 240;
const ROW_OVERSCAN = 6;
const VISIBLE_ROW_COUNT =
  Math.ceil(DROPDOWN_VIEWPORT / ROW_HEIGHT) + ROW_OVERSCAN * 2;

// ── Format ISO datetime → HH:mm ──
const formatTime = (value) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return TIME_FORMATTER.format(date);
};

// ── Status badge class ──
const statusClass = (status) =>
  `report-status-badge report-status-${String(status || "")
    .toLowerCase()
    .replace(/\s+/g, "-")}`;

// ── Calculate total working hours from first and last punch ──
const calculateTotalHours = (firstPunch, lastPunch) => {
  if (!firstPunch || !lastPunch) {
    return "-";
  }

  const start = new Date(firstPunch);
  const end = new Date(lastPunch);

  if (
    Number.isNaN(start.getTime()) ||
    Number.isNaN(end.getTime()) ||
    end < start
  ) {
    return "-";
  }

  const diffMinutes = Math.floor((end - start) / (1000 * 60));

  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;

  return `${hours}h ${String(minutes).padStart(2, "0")}m`;
};

// ── Results table ──
// Memoised and lifted out of Report() so that typing in the Search User field
// — which updates state on the parent — cannot force every row to re-render.
// It only re-renders when the `rows` array identity actually changes.
const ReportTable = React.memo(function ReportTable({ rows }) {
  return (
    <div className="report-table-wrapper">
      <table className="report-table">
        <thead>
          <tr>
            <th className="report-serial-col">Sr. No.</th>

            <th className="report-empcode-col">Employee Code</th>

            <th className="report-name-col">Name</th>

            <th className="report-date-col">Date</th>

            <th className="report-time-col">In Time</th>

            <th className="report-location-col">Location (In)</th>

            <th className="report-time-col">Out Time</th>

            <th className="report-location-col">Location (Out)</th>

            <th className="report-totalhr-col">Total Hr</th>

            <th className="report-status-col">Status</th>
          </tr>
        </thead>

        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan="10" className="report-no-data">
                No records found.
              </td>
            </tr>
          ) : (
            rows.map((row, index) => (
              <tr key={`${row.user_id}-${row.date}-${index}`}>
                <td className="report-serial-col">{index + 1}</td>

                <td className="report-empcode-col">{row.employee_code}</td>

                <td className="report-name-col">{row.name}</td>

                <td className="report-date-col">{row.date_label}</td>

                <td className="report-time-col">{row.in_label}</td>

                <td className="report-location-col">{row.in_location}</td>

                <td className="report-time-col">{row.out_label}</td>

                <td className="report-location-col">{row.out_location}</td>

                <td className="report-totalhr-col">{row.total_hr || "-"}</td>

                <td className="report-status-col">
                  <span className={row.status_class}>{row.status_label}</span>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
});

export default function Report() {
  const fromDateRef = useRef(null);
  const toDateRef = useRef(null);
  const searchRef = useRef(null);
  const userSearchInputRef = useRef(null);
  const userListRequestedRef = useRef(false);
  const dropdownRef = useRef(null);
  const locationCacheRef = useRef(new Map());

  const { fd, td } = getDefaultMonth();

  const [fromDate, setFromDate] = useState(fd);
  const [toDate, setToDate] = useState(td);
  const [searchUser, setSearchUser] = useState("");
  const [rows, setRows] = useState([]);

  const [userList, setUserList] = useState([]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [isUserListLoading, setIsUserListLoading] = useState(false);
  const [hasLoadedUserList, setHasLoadedUserList] = useState(false);
  const [userListError, setUserListError] = useState("");
  const [dropdownScrollTop, setDropdownScrollTop] = useState(0);

  const { user } = useAuthStore();
  const { showLoader, hideLoader } = useLoaderStore();
  const companyId = user?.company_id;

  // Reset the lazy-loaded list when the logged-in company changes.
  useEffect(() => {
    setUserList([]);
    setHasLoadedUserList(false);
    setUserListError("");
    userListRequestedRef.current = false;
  }, [companyId]);

  // ── Lazy-load users only after the Search User field is opened ──
  const loadUserList = async (force = false) => {
    if (!companyId) return;

    // A failed attempt used to leave `hasLoadedUserList` false, which meant the
    // onChange handler re-fired this request on every keystroke. The ref is set
    // synchronously and only the Retry button (force) clears it.
    if (userListRequestedRef.current && !force) return;

    userListRequestedRef.current = true;

    try {
      setIsUserListLoading(true);
      setUserListError("");

      const response = await api.get(`/user/list/${companyId}`);
      const result = await response.json();
      const list = Array.isArray(result)
        ? result
        : Array.isArray(result?.data)
          ? result.data
          : [];

      // Derive the display name and a single lowercase haystack up front, then
      // sort once. Previously both happened inside the render body, so every
      // keystroke rebuilt these strings and re-sorted an already-sorted list.
      const prepared = list.map((u) => {
        const name = `${u.first_name || ""} ${u.last_name || ""}`.trim();

        return {
          ...u,
          display_name: name || "-",
          search_text: `${name} ${u.employee_code || ""}`.toLowerCase(),
        };
      });

      prepared.sort((a, b) =>
        CODE_COLLATOR.compare(
          String(a.employee_code || ""),
          String(b.employee_code || ""),
        ),
      );

      console.info("REPORT USER SEARCH: users loaded", {
        companyId,
        count: prepared.length,
      });

      setUserList(prepared);
      setHasLoadedUserList(true);
    } catch (error) {
      console.error("REPORT USER SEARCH: user-list request failed", {
        companyId,
        error: error.message,
      });
      setUserListError(error.message || "Unable to load users");
    } finally {
      setIsUserListLoading(false);
    }
  };

  // ── Close the user dropdown only when clicking outside the search field,
  // so picking one user doesn't close it before picking the next ──
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ── Users matching the current search text ──
  // The filter runs against a deferred copy of the query so React can paint the
  // typed character first and rebuild the list in a follow-up render.
  const deferredSearchUser = useDeferredValue(searchUser);
  const deferredQuery = deferredSearchUser.trim().toLowerCase();

  // Every match, uncapped — this is what the dropdown scrolls through.
  const suggestedUsers = useMemo(
    () =>
      deferredQuery
        ? userList.filter((u) => u.search_text.includes(deferredQuery))
        : userList,
    [userList, deferredQuery],
  );

  // Only the slice under the scroll window is actually mounted, so the number
  // of <li> nodes stays constant no matter how many employees match.
  const visibleRange = useMemo(() => {
    // Clamped at both ends. Without the upper clamp a stale offset — e.g. the
    // frame after a query narrows the list but before the reset effect runs —
    // would window past the last match and mount no rows at all.
    const maxStart = Math.max(0, suggestedUsers.length - VISIBLE_ROW_COUNT);

    const start = Math.min(
      maxStart,
      Math.max(0, Math.floor(dropdownScrollTop / ROW_HEIGHT) - ROW_OVERSCAN),
    );

    return {
      start,
      users: suggestedUsers.slice(start, start + VISIBLE_ROW_COUNT),
    };
  }, [suggestedUsers, dropdownScrollTop]);

  // O(1) membership test for the dropdown's "selected" class, instead of an
  // Array.some() scan per rendered row.
  const selectedIds = useMemo(
    () => new Set(selectedUsers.map((u) => u.id)),
    [selectedUsers],
  );

  // A new query produces a different list, so an old scroll offset would leave
  // the user staring at blank space. Snap back to the top.
  useEffect(() => {
    setDropdownScrollTop(0);

    if (dropdownRef.current) {
      dropdownRef.current.scrollTop = 0;
    }
  }, [deferredQuery]);

  // Closing the dropdown unmounts the scroll container, which throws away its
  // DOM scrollTop — but `dropdownScrollTop` lives on this component and
  // survives. Reopening therefore remounted at scrollTop 0 while the
  // virtualiser still windowed from the old offset, mounting rows below the
  // visible band: an empty dropdown with a full-length scrollbar. Resetting in
  // the same update as the open keeps the two in step.
  const openUserDropdown = () => {
    if (!showUserDropdown) {
      setDropdownScrollTop(0);

      if (dropdownRef.current) {
        dropdownRef.current.scrollTop = 0;
      }
    }

    setShowUserDropdown(true);
    loadUserList();
  };

  const toggleSelectUser = (u) => {
    const name =
      u.display_name ||
      `${u.first_name || ""} ${u.last_name || ""}`.trim() ||
      "-";

    setSelectedUsers((prev) =>
      prev.some((p) => p.id === u.id)
        ? prev.filter((p) => p.id !== u.id)
        : [...prev, { id: u.id, name, employee_code: u.employee_code || "" }],
    );

    // The query has done its job once the chip exists. Leaving it behind meant
    // the next name had to be typed after it, and worse, handleSearch treated
    // the leftover text as an extra result filter — which is why picking two
    // people only ever showed the last one.
    setSearchUser("");
    userSearchInputRef.current?.focus();
  };

  const removeSelectedUser = (id) => {
    setSelectedUsers((prev) => prev.filter((p) => p.id !== id));
  };

  const handleClearSearch = () => {
    setSearchUser("");
    setSelectedUsers([]);
  };

  // ── Search handler ──
  const handleSearch = async () => {
    if (!fromDate || !toDate) {
      toast.warning("Please select From Date and To Date");
      return;
    }

    if (new Date(fromDate) > new Date(toDate)) {
      toast.warning("From Date cannot be after To Date");
      return;
    }

    if (!companyId) {
      toast.error("Company ID not found");
      return;
    }

    try {
      showLoader("master");
      setRows([]);
      const params = new URLSearchParams();

      params.append("company_id", companyId);
      params.append("from_date", fromDate);
      params.append("to_date", toDate);

      selectedUsers.forEach((u) => params.append("user_id", u.id));

      const response = await api.get(
        `/attendance/attendance-log?${params.toString()}`,
      );

      const result = await response.json();

      console.log("ATTENDANCE REPORT RESPONSE:", result);

      // ── API response format ──
      // {
      //   status: true,
      //   message: "Success",
      //   total_records: 99,
      //   users_included: 9,
      //   data: [...]
      // }

      const apiRows = Array.isArray(result?.data) ? result.data : [];

      // ── Convert API response to table format ──
      const mappedRows = apiRows.map((row) => ({
        user_id: row.user_id,
        employee_code: row.employee_code,
        name: row.employee_name,
        date: row.date,
        in_time: row.first_punch,
        in_location: row.in_location_name || row.in_location || "-",
        out_time: row.last_punch,
        out_location: row.out_location_name || row.out_location || "-",
        total_hr: calculateTotalHours(row.first_punch, row.last_punch),
        status: row.type,
        // Display strings are built once here so the table renders them as
        // plain property reads rather than re-formatting on every render.
        date_label: formatDate(row.date),
        in_label: formatTime(row.first_punch),
        out_label: formatTime(row.last_punch),
        status_label: row.type || "-",
        status_class: statusClass(row.type),
      }));

      // ── Filter by selected users / typed search text, then sort date-wise ──
      // Explicitly picked users win: when chips are present they are the whole
      // filter, and any text still sitting in the box is ignored. Applying both
      // used to intersect them, dropping every selected user but the one whose
      // name matched the leftover query.
      const selectedIdSet = new Set(selectedUsers.map((u) => u.id));
      const q = selectedIdSet.size > 0 ? "" : searchUser.trim().toLowerCase();

      const filtered = mappedRows
        .filter((row) => {
          if (selectedIdSet.size > 0) {
            return selectedIdSet.has(row.user_id);
          }

          if (!q) return true;

          return (
            String(row.name || "")
              .toLowerCase()
              .includes(q) ||
            String(row.employee_code || "")
              .toLowerCase()
              .includes(q) ||
            String(row.user_id || "")
              .toLowerCase()
              .includes(q) ||
            String(row.date || "")
              .toLowerCase()
              .includes(q)
          );
        })
        .sort((a, b) => {
          if (a.date !== b.date) {
            return a.date < b.date ? -1 : 1;
          }
          return String(a.name || "").localeCompare(String(b.name || ""));
        });

      setRows(await resolveReportLocations(filtered, locationCacheRef.current));

      if (filtered.length === 0) {
        toast.info("No records found for the selected filters");
      }
    } catch (error) {
      console.error("ATTENDANCE REPORT ERROR:", error);

      toast.error(error.message || "Failed to fetch attendance report");

      setRows([]);
    } finally {
      hideLoader();
    }
  };

  // ── Export to Excel ──
  const handleExportExcel = async () => {
    if (rows.length === 0) {
      toast.warning("No data to export");
      return;
    }

    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Report");

      // ── Info block: Report Name / Account Name / Run Date & Time / Period ──
      // Each label sits in column A, and its ": value" is merged across the
      // rest of the row so long values (e.g. the company name) aren't cut off.
      const addInfoRow = (label, value) => {
        const row = worksheet.addRow([label, `: ${value}`]);
        worksheet.mergeCells(row.number, 2, row.number, EXPORT_COLUMN_COUNT);
        row.font = { bold: true };
        return row;
      };

      const now = new Date();
      const runDateTime = `${formatDateSlash(
        `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
          now.getDate(),
        ).padStart(2, "0")}`,
      )} ${String(now.getHours()).padStart(2, "0")}:${String(
        now.getMinutes(),
      ).padStart(2, "0")}`;

      const accountName = EXPORT_ACCOUNT_NAMES[Number(companyId)] || "-";

      addInfoRow("Report Name", "Attendance Detail By Employee Name");
      addInfoRow("Account Name", accountName);
      addInfoRow("Run Date & Time", runDateTime);
      addInfoRow(
        "Period",
        `${formatDateSlash(fromDate)} To ${formatDateSlash(toDate)}`,
      );

      worksheet.addRow([]); // blank spacer row before the data table

      // ── Table header row, styled green to match the rest of the report ──
      const headerRow = worksheet.addRow([
        "Sr. No.",
        "Employee Code",
        "Name",
        "Date",
        "In Time",
        "Location (In)",
        "Out Time",
        "Location (Out)",
        "Total Hr",
        "Status",
      ]);
      headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF2E7D32" },
        };
        cell.alignment = { vertical: "middle", horizontal: "center" };
      });

      rows.forEach((row, index) => {
        worksheet.addRow([
          index + 1,
          row.employee_code,
          row.name,
          row.date_label,
          row.in_label,
          row.in_location,
          row.out_label,
          row.out_location,
          row.total_hr || "-",
          row.status_label,
        ]);
      });

      // Column widths so Excel opens with a nice layout
      worksheet.columns = [
        { width: 8 }, // Sr. No.
        { width: 16 }, // Employee Code
        { width: 22 }, // Name
        { width: 14 }, // Date
        { width: 10 }, // In Time
        { width: 24 }, // Location (In)
        { width: 10 }, // Out Time
        { width: 24 }, // Location (Out)
        { width: 12 }, // Total Hr
        { width: 14 }, // Status
      ];

      // ── Generate the file as a binary buffer ──
      const fileData = await workbook.xlsx.writeBuffer();

      // ── Wrap in a proper Blob with the correct MIME type ──
      const blob = new Blob([fileData], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      // ── Trigger the download via an anchor element ──
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = url;
      link.download = `Attendance_Report_${fromDate}_to_${toDate}.xlsx`;

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);

      URL.revokeObjectURL(url);

      toast.success("Excel exported successfully");
    } catch (error) {
      console.error("EXPORT ERROR:", error);

      toast.error("Failed to export Excel");
    }
  };

  return (
    <div className="report-container">
      {/* ===== TOP BAR ===== */}
      <div className="report-topbar">
        <PageHeading className="report-title">Attendance Report</PageHeading>

        <button type="button" className="excel-btn" onClick={handleExportExcel}>
          <svg
            className="excel-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="9" y1="15" x2="15" y2="15" />
            <line x1="9" y1="18" x2="15" y2="18" />
          </svg>
          Export Excel
        </button>
      </div>

      {/* ===== FILTER ROW ===== */}
      <div className="report-filter-row">
        {/* From Date */}
        <div className="report-filter-field">
          <label>From Date</label>

          <div className="report-date-wrapper">
            <input
              type="text"
              className="report-date-display"
              value={fromDate ? fromDate.split("-").reverse().join("-") : ""}
              placeholder="dd-mm-yyyy"
              readOnly
              onClick={() => fromDateRef.current?.showPicker()}
            />

            <FiCalendar
              className="report-calendar-icon"
              onClick={() => fromDateRef.current?.showPicker()}
            />

            <input
              ref={fromDateRef}
              type="date"
              className="report-date-picker"
              min="2000-01-01"
              max="2099-12-31"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>
        </div>

        {/* To Date */}
        <div className="report-filter-field">
          <label>To Date</label>

          <div className="report-date-wrapper">
            <input
              type="text"
              className="report-date-display"
              value={toDate ? toDate.split("-").reverse().join("-") : ""}
              placeholder="dd-mm-yyyy"
              readOnly
              onClick={() => toDateRef.current?.showPicker()}
            />

            <FiCalendar
              className="report-calendar-icon"
              onClick={() => toDateRef.current?.showPicker()}
            />

            <input
              ref={toDateRef}
              type="date"
              className="report-date-picker"
              min="2000-01-01"
              max="2099-12-31"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
        </div>

        {/* Search User */}
        <div
          className="report-filter-field report-search-field"
          ref={searchRef}
        >
          <label>Search User</label>

          <div
            className="report-search-wrapper"
            onClick={(event) => {
              if (event.target.closest("button")) return;
              userSearchInputRef.current?.focus();
              openUserDropdown();
            }}
          >
            <svg
              className="report-search-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>

            <div className="report-search-scroll">
              {selectedUsers.map((u) => (
                <span key={u.id} className="report-chip">
                  {u.name}
                  <button
                    type="button"
                    className="report-chip-remove"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => removeSelectedUser(u.id)}
                    aria-label={`Remove ${u.name}`}
                  >
                    ×
                  </button>
                </span>
              ))}

              <input
                ref={userSearchInputRef}
                type="text"
                className="report-search-input"
                placeholder={
                  selectedUsers.length > 0 ? "" : "Search members..."
                }
                value={searchUser}
                onChange={(e) => {
                  setSearchUser(e.target.value);
                  openUserDropdown();
                }}
                onFocus={() => {
                  openUserDropdown();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setShowUserDropdown(false);
                    handleSearch();
                  }
                }}
              />
            </div>

            {(searchUser || selectedUsers.length > 0) && (
              <button
                type="button"
                className="report-search-clear"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleClearSearch}
                aria-label="Clear search"
              >
                ×
              </button>
            )}

            {showUserDropdown && (
              <div
                className="report-user-dropdown"
                ref={dropdownRef}
                onScroll={(e) =>
                  setDropdownScrollTop(e.currentTarget.scrollTop)
                }
              >
                {isUserListLoading ? (
                  <div className="report-user-dropdown-message">
                    Loading users...
                  </div>
                ) : userListError ? (
                  <div className="report-user-dropdown-message error">
                    {userListError}
                    <button type="button" onClick={() => loadUserList(true)}>
                      Retry
                    </button>
                  </div>
                ) : suggestedUsers.length > 0 ? (
                  <ul
                    className="report-user-dropdown-list"
                    style={{ height: suggestedUsers.length * ROW_HEIGHT }}
                  >
                    {visibleRange.users.map((u, i) => (
                      <li
                        key={u.id}
                        className={`report-user-dropdown-item ${
                          selectedIds.has(u.id) ? "selected" : ""
                        }`}
                        style={{ top: (visibleRange.start + i) * ROW_HEIGHT }}
                        onMouseDown={(e) => {
                          // Stop the mousedown from blurring the input so the
                          // next name can be typed without re-clicking.
                          e.preventDefault();
                          toggleSelectUser(u);
                        }}
                      >
                        {/* <input
                      type="checkbox"
                      className="report-user-dropdown-checkbox"
                      checked={selectedUsers.some((p) => p.id === u.id)}
                      readOnly
                    /> */}
                        <span className="report-user-dropdown-name">
                          {u.display_name}
                          {u.employee_code ? ` | ${u.employee_code}` : ""}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : hasLoadedUserList ? (
                  <div className="report-user-dropdown-message">
                    No users found
                  </div>
                ) : (
                  // Reached when the roster never started loading — usually
                  // because the auth store has not resolved a company yet.
                  // Previously this branch rendered null, i.e. an empty box.
                  <div className="report-user-dropdown-message">
                    {companyId ? "Loading users..." : "No company selected"}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Search Button */}
        <div className="report-filter-field report-btn-field">
          <button
            type="button"
            className="report-search-btn"
            onClick={handleSearch}
          >
            Search
          </button>
        </div>
      </div>

      {/* ===== TABLE ===== */}
      <ReportTable rows={rows} />
    </div>
  );
}
